import React, { useState, useEffect } from 'react';
import { MpesaService, CurrencyService, DonationAnalytics } from '../services/donationService';

const MpesaButton = ({ 
  amount, 
  phoneNumber, 
  onSuccess, 
  onError, 
  disabled = false,
  currency = 'USD'
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [kesAmount, setKesAmount] = useState(null);

  // Convert USD to KES if needed
  useEffect(() => {
    if (amount && currency === 'USD') {
      CurrencyService.convertUsdToKes(amount)
        .then(setKesAmount)
        .catch(console.error);
    } else if (amount && currency === 'KES') {
      setKesAmount(amount);
    }
  }, [amount, currency]);

  const handleMpesaPayment = async () => {
    console.log('🚀 M-Pesa payment initiated with:', { amount, phoneNumber, currency });
    
    if (!amount || !phoneNumber) {
      const error = new Error('Amount and phone number are required');
      console.error('❌ M-Pesa validation error:', error.message);
      if (onError) onError(error);
      return;
    }

    if (!MpesaService.validatePhoneNumber(phoneNumber)) {
      const error = new Error('Invalid phone number format. Please use format: 254XXXXXXXXX or 07XXXXXXXX');
      console.error('❌ M-Pesa phone validation error:', error.message);
      if (onError) onError(error);
      return;
    }

    setIsProcessing(true);
    console.log('⏳ Starting M-Pesa STK Push...');

    try {
      // Always use KES for M-Pesa, convert if necessary
      const finalAmount = currency === 'KES' ? amount : (kesAmount || amount);
      console.log('💰 Final amount for M-Pesa:', finalAmount, 'KES');
      
      DonationAnalytics.trackDonationAttempt('mpesa', finalAmount, 'KES');

      console.log('📞 Calling MpesaService.initiateStkPush...');
      const response = await MpesaService.initiateStkPush(
        finalAmount,
        phoneNumber,
        `WeFlare-Donation-${Date.now()}`
      );

      console.log('📱 M-Pesa STK Push response:', response);

      if (response.ResponseCode === '0') {
        console.log('✅ STK Push sent successfully!');
        
        // Show intermediate success message
        const intermediateResult = {
          success: false, // Don't redirect yet
          pending: true,
          message: 'Payment request sent to your phone. Please check your phone and enter your M-Pesa PIN to complete the donation.',
          checkoutRequestId: response.CheckoutRequestID,
          merchantRequestId: response.MerchantRequestID,
          amount: finalAmount,
          phoneNumber: phoneNumber
        };

        // Start polling for payment status
        console.log('🔄 Starting payment status polling...');
        MpesaService.pollPaymentStatus(response.CheckoutRequestID, (status) => {
          console.log('📊 Payment status update:', status);
          
          if (status.success) {
            console.log('🎉 Payment confirmed successful!');
            DonationAnalytics.trackDonationSuccess('mpesa', finalAmount, 'KES', response.CheckoutRequestID);
            
            if (onSuccess) {
              onSuccess({
                success: true,
                message: 'Payment completed successfully! Thank you for your donation.',
                checkoutRequestId: response.CheckoutRequestID,
                amount: finalAmount,
                phoneNumber: phoneNumber,
                transactionId: status.transactionId,
                receiptNumber: status.receiptNumber
              });
            }
          } else if (status.failed) {
            console.log('❌ Payment confirmed failed');
            if (onError) {
              onError(new Error(status.message || 'Payment was cancelled or failed. Please try again.'));
            }
          }
          // If still pending, continue polling
        });

        // Show intermediate state to user
        if (onSuccess) {
          onSuccess(intermediateResult);
        }

      } else {
        console.error('❌ STK Push failed with response:', response);
        throw new Error(response.ResponseDescription || 'M-Pesa payment failed');
      }

    } catch (error) {
      console.error('💥 M-Pesa payment error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response ? error.response.data : 'No response data',
        status: error.response ? error.response.status : 'No status'
      });
      
      DonationAnalytics.trackDonationError('mpesa', error.message);

      // Show user-friendly error message
      let userMessage = 'Payment failed. Please try again.';
      if (error.message.includes('Backend server is not running')) {
        userMessage = 'Payment server is not available. Please contact support or try again later.';
      } else if (error.message.includes('Cannot connect to payment server')) {
        userMessage = 'Unable to connect to payment server. Please check your internet connection and try again.';
      } else if (error.message.includes('network') || error.message.includes('timeout')) {
        userMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('phone')) {
        userMessage = 'Invalid phone number. Please check and try again.';
      } else if (error.message.includes('insufficient')) {
        userMessage = 'Insufficient funds. Please check your M-Pesa balance.';
      } else if (error.message.includes('Authentication failed')) {
        userMessage = 'Payment system error. Please contact support.';
      } else if (error.message.includes('Server error')) {
        userMessage = 'Payment server error. Please try again in a few minutes.';
      }

      if (onError) {
        onError(new Error(userMessage));
      }
    } finally {
      console.log('🏁 M-Pesa payment process completed, stopping loading...');
      setIsProcessing(false);
    }
  };

  const displayAmount = kesAmount ? `KES ${kesAmount.toLocaleString()}` : `${currency} ${amount}`;
  const showConversion = currency === 'USD' && kesAmount && kesAmount !== amount;

  return (
    <div className="mpesa-button-container">
      <button
        className="btn btn-success w-100 d-flex align-items-center justify-content-center"
        onClick={handleMpesaPayment}
        disabled={disabled || isProcessing || !amount || !phoneNumber}
        style={{ height: '55px' }}
      >
        {isProcessing ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Processing...
          </>
        ) : (
          <>
            <i className="bi bi-phone me-2"></i>
            Pay {displayAmount} with M-Pesa
          </>
        )}
      </button>
      
      {showConversion && (
        <small className="text-muted mt-2 d-block text-center conversion-rate">
          <i className="bi bi-arrow-left-right me-1"></i>
          Converted from ${amount} USD
        </small>
      )}
    </div>
  );
};

export default MpesaButton;